/**
 * Table column utilities for reporting components
 *
 * Provides utilities for column reordering and column management
 * for dynamic table rendering with persistent saved reports integration.
 *
 * **Field Format Notes:**
 * - columnOrder arrays use snake_case format (matches OpenSearch field names)
 * - columns array keys are camelCase (matches API response data)
 * - keyConverter function converts between formats (using resultKey from /fields)
 */

import type { FieldOption } from './opensearch-field-mapper';
import { snakeToCamelCase } from 'tio-common/utils/string-case-conversion';

/**
 * Build a lookup map from snake_case field path → camelCase result key
 * using the resultKey provided by the /fields API.
 *
 * This eliminates the need for frontend case-conversion logic — the API
 * tells us exactly what key each field uses in search result documents.
 */
export function buildResultKeyLookup(availableFields?: FieldOption[]): Map<string, string> {
  const map = new Map<string, string>();
  if (!availableFields) return map;
  for (const field of availableFields) {
    if (field.resultKey) {
      // Map both path and key (name) to resultKey
      if (field.path) map.set(field.path, field.resultKey);
      if (field.key && field.key !== field.path) map.set(field.key, field.resultKey);
    }
  }
  return map;
}

/**
 * Look up the camelCase result key for a snake_case field path.
 * Uses the /fields API-provided resultKey when available.
 * Falls back to snakeToCamelCase conversion for backward compatibility
 * (e.g. fields not yet in /fields, or tests without resultKey data).
 */
export function getResultKey(fieldPath: string, lookup: Map<string, string>): string {
  return (
    lookup.get(fieldPath) || (fieldPath.includes('_') ? snakeToCamelCase(fieldPath) : fieldPath)
  );
}

/**
 * Column object interface - generic to work with any column structure
 * (DisplayColumn, ColumnLike, or similar)
 */
interface Column {
  readonly key: string;
  [key: string]: unknown;
}

/**
 * Reorder columns based on a specified order array
 *
 * Takes an array of columns (with camelCase keys) and a desired order array
 * (with snake_case keys). Returns columns in the specified order, with any
 * unspecified columns appended to the end.
 *
 * **Example:**
 * ```
 * columns = [
 *   { key: 'employeeNameKeyword', label: 'Employee Name' },
 *   { key: 'tasApplicationIdKeyword', label: 'Application ID' },
 *   { key: 'createdAtDate', label: 'Created At' }
 * ]
 *
 * columnOrder = [
 *   'tas_application_id_keyword',  // How it's stored in reports
 *   'employee_name_keyword'
 * ]
 *
 * Result:
 * [
 *   { key: 'tasApplicationIdKeyword', label: 'Application ID' },     // Re-ordered first
 *   { key: 'employeeNameKeyword', label: 'Employee Name' },          // Re-ordered second
 *   { key: 'createdAtDate', label: 'Created At' }                    // Appended (not in order)
 * ]
 * ```
 *
 * @param columns - Available columns (with camelCase keys)
 * @param columnOrder - Desired order as snake_case keys (how they're stored in reports)
 * @param keyConverter - Function to convert snake_case to camelCase (e.g., snakeToCamelCase)
 * @returns Reordered columns with same data, new order
 */
export function reorderTableColumns<T extends Column>(
  columns: T[],
  columnOrder: string[],
  keyConverter: (snakeKey: string) => string
) {
  if (!columnOrder || columnOrder.length === 0) {
    return columns; // Return as-is if no order specified
  }

  // Create a map of camelCase keys to columns for lookup
  const columnMap = new Map<string, T>();
  columns.forEach((col) => {
    columnMap.set(col.key as string, col);
  });

  // Build ordered result: process columnOrder (snake_case), lookup by converted camelCase
  const orderedColumns: T[] = [];
  const seenKeys = new Set<string>();

  // First, add columns in the specified order
  columnOrder.forEach((snakeKey) => {
    const camelKey = keyConverter(snakeKey);
    const column = columnMap.get(camelKey);
    if (column) {
      orderedColumns.push(column);
      seenKeys.add(camelKey);
    }
  });

  // Then, add any remaining columns not in the order array
  columns.forEach((col) => {
    if (!seenKeys.has(col.key as string)) {
      orderedColumns.push(col);
    }
  });

  return orderedColumns;
}
